/**
 * Per-session user overrides on what publish does ("planned actions"). Click a
 * session in the plan calendar and pin it:
 *   "protect" — never delete this event on publish (overrides a planned removal)
 *   "remove"  — delete this event / don't create this session on publish
 * Keyed by date + name (lowercased) so the same key works for plan sessions,
 * payloads and calendar events. Persisted in localStorage; sticky across
 * regenerations and publishes (so a "remove" stays removed each time).
 */
export type PlanAction = "protect" | "remove";
export type PlanActionOverrides = Record<string, PlanAction>;

const KEY = "plan-action-overrides";

export const overrideKey = (date: string, name: string): string =>
  `${date}|${(name || "").trim().toLowerCase()}`;

export function loadPlanActionOverrides(): PlanActionOverrides {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; }
}

/** Set (or clear, when value is null) the override for a session and persist. */
export function setPlanActionOverride(date: string, name: string, value: PlanAction | null): PlanActionOverrides {
  const all = loadPlanActionOverrides();
  const k = overrideKey(date, name);
  if (value == null) delete all[k];
  else all[k] = value;
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch { /* ignore */ }
  return { ...all };
}
